import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Music, Star, Award } from 'lucide-react';
import Header from '../components/Header';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
            About BandCam.io
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            <h2>Bringing Families and School Bands Together</h2>
            We connect communities through personalized video performances that turn celebrations into lifelong memories.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 font-display">Our Mission</h2>
              <p className="text-lg text-gray-300 mb-6">
              We believe music is a way to bring people together and capture memories that last forever. 
              BandCam.io connects families with their local school bands, creating personalized video messages that make every milestone(holidays, birthdays, graduations) truly unforgettable.
              </p>
              <div className="flex items-center text-goldie">
                <Heart className="w-6 h-6 mr-2" />
                <span className="text-lg font-semibold">Making life's milestones musical since 2025</span>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/band4.png" 
                alt="School Band Performance" 
                className="w-full h-80 object-cover border-4 border-goldie"
              />
              <div className="absolute inset-0 bg-black bg-opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center font-display">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Music className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-4">Musical Excellence</h3>
              <p className="text-gray-700">
                We work with dedicated student musicians who deliver high-quality performances families will treasure.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-4">Community Connection</h3>
              <p className="text-gray-700">
                Every video strengthens the bond between schools and families, keeping communities connected through music.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-4">Personal Touch</h3>
              <p className="text-gray-700">
                Each message is crafted with care, making every video personal, heartfelt, and perfectly suited to the occasion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="/fam.png" 
                alt="School Band Performance" 
                className="w-full h-90 object-cover border-4 border-goldie"
              />
              <div className="absolute inset-0 bg-black bg-opacity-20"></div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 font-display">Our Story</h2>
              <p className="text-lg text-gray-300 mb-6">
                I've been a proud band dad for over a decade, and my wife even served as band booster president. 
                We've seen up close the incredible impact band can have on kids—it gives them a sense of community, teaches teamwork, and builds real leadership skills.
              </p>
              <p className="text-lg text-gray-300 mb-6">
                We've also seen the other side: how expensive it is to keep a band running, and how little support school districts provide. 
                Bands often run a dozen fundraisers every year, bringing in anywhere from a few hundred to a few thousand dollars at a time. 
                Most of those fundraisers involve selling physical products, and the band only sees a small slice of the profits.
              </p>
              <p className="text-lg text-gray-300 mb-6">
                As a software engineer of 30 years, I know the difference digital products can make. 
                So I decided to combine my two passions, band and technology, and create BandCam.io. 
                It's a way for bands to raise money more effectively, while families get something truly meaningful in return: a personalized performance they'll never forget.
              </p>
            </div>
            
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center font-display">By the Numbers</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-goldie mb-2">TBD</div>
              <p className="text-gray-700">Participating Schools</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-goldie mb-2">TBD</div>
              <p className="text-gray-700">Happy Families</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-goldie mb-2">TBD</div>
              <p className="text-gray-700">Videos Created</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-goldie mb-2">TBD</div>
              <p className="text-gray-700">Average Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center font-display">Meet Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-32 h-32 bg-goldie rounded-full mx-auto mb-4 flex items-center justify-center">
              <img 
                src="/ceo.jpg" 
                alt="School Band Performance" 
                className="rounded-full object-cover border-4 border-goldie"
              />
              </div>
              <h3 className="text-xl font-bold mb-2">Keith Kritselis</h3>
              <p className="text-goldie mb-4">Founder & CEO</p>
              <p className="text-gray-300">
                Band dad and software engineer.
              </p>
            </div>
            <div className="text-center">
              <div className="w-32 h-32 bg-goldie rounded-full mx-auto mb-4 flex items-center justify-center">
                <Music className="w-16 h-16 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">TBD</h3>
              <p className="text-goldie mb-4">Head of Music Partnerships</p>
              <p className="text-gray-300">
                Works directly with school bands to ensure quality and creativity in every video.
              </p>
            </div>
            <div className="text-center">
              <div className="w-32 h-32 bg-goldie rounded-full mx-auto mb-4 flex items-center justify-center">
                <Award className="w-16 h-16 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">TBD</h3>
              <p className="text-goldie mb-4">Customer Experience</p>
              <p className="text-gray-300">
                Ensures every family has an exceptional experience from order to delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-midnight text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">
            Ready to Create Your Musical Memory?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Join thousands of families who have celebrated their special moments with BandCam.io
          </p>
          <Link
            to="/schools"
            className="btn bg-goldie text-black hover:bg-opacity-90 text-lg px-8 py-4 border-2 border-black font-bold"
          >
            Find Your School
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
              <div className="flex space-x-4">
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
                <Star className="w-5 h-5 text-goldie" />
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/faq" className="text-gray-300 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
